import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/** What the cases match against: the wired customer's id, or a text value. */
export const MAP_VALUE_MATCH_KINDS = ["customer", "text"] as const;
export type MapValueMatchKind = (typeof MAP_VALUE_MATCH_KINDS)[number];

/** What the cases return: a user id (picked in the editor) or plain text. */
export const MAP_VALUE_OUTPUT_KINDS = ["user", "text"] as const;
export type MapValueOutputKind = (typeof MAP_VALUE_OUTPUT_KINDS)[number];

export const MapValueCaseSchema = z.object({
  /** Stable row id, so the editor can key cases that move or get removed. */
  id: z.string().optional(),
  /** Display name for the matched value (e.g. the customer's name). */
  label: z.string().optional(),
  /** The customer id or text the input must equal. */
  equals: z.string().min(1),
  /** What the step returns when this case matches (a user id or text). */
  output: z.string().min(1),
  /** Display name for the output (e.g. the user's name). */
  outputLabel: z.string().optional(),
});

export type MapValueCase = z.infer<typeof MapValueCaseSchema>;

/**
 * Map Value Step
 *
 * A Switch that returns a value instead of choosing a branch: the input is
 * compared against an ordered list of cases, and the first case it equals
 * gives the output. No match gives the default output, or nothing.
 *
 * ```
 * Action Input (customer) → Map Value [ SAFEWAY → Kevin, Winco → Brian ]
 *                                     → Action Output (assignee)
 * ```
 *
 * Each match kind has its own typed input socket, so a Customer input can be
 * wired straight from an Action Input port typed as a customer.
 */
export const mapValueStep = defineStepConfig({
  type: "map_value",
  name: "Map Value",
  description:
    "Return a value by matching the input against an ordered list of cases (like a Switch that returns a value)",
  category: "Transformation",

  inputSchema: z.object({
    customer: entityOfType(SystemEntity.AR_CUSTOMER)
      .optional()
      .describe("The customer to match, when matching on customer"),
    text: z
      .string()
      .optional()
      .describe("The text to match, when matching on text"),
  }),

  outputSchema: z.object({
    found: z.boolean().describe("Whether a case (or the default) matched"),
    value: z
      .string()
      .optional()
      .describe("The matched case's output, else the default"),
  }),

  configSchema: z.object({
    matchOn: z
      .enum(MAP_VALUE_MATCH_KINDS)
      .default("customer")
      .describe("Match the cases against the customer or a text value"),
    outputKind: z
      .enum(MAP_VALUE_OUTPUT_KINDS)
      .default("user")
      .describe("Whether the cases return a user or text"),
    cases: z
      .array(MapValueCaseSchema)
      .default([])
      .describe("Ordered cases; the first one the input equals wins"),
    defaultOutput: z
      .string()
      .optional()
      .describe("Returned when no case matches; leave empty for nothing"),
    defaultOutputLabel: z.string().optional(),
  }),

  emits: [] as const,
});

/** The value the cases are compared against, from the configured input. */
export function mapValueKey(
  input: { customer?: { entityId: string }; text?: string },
  matchOn: MapValueMatchKind,
): string | null {
  const raw = matchOn === "customer" ? input.customer?.entityId : input.text;
  const key = raw?.trim();
  return key ? key : null;
}

/**
 * First case whose `equals` is the key (text compared case-insensitively),
 * else the default. Pure, shared by the handler and its tests.
 */
export function resolveMapValue(
  key: string | null,
  config: {
    matchOn: MapValueMatchKind;
    cases: readonly MapValueCase[];
    defaultOutput?: string;
  },
): { found: boolean; value?: string } {
  const normalize = (value: string) =>
    config.matchOn === "text" ? value.trim().toLowerCase() : value.trim();
  const match =
    key === null
      ? undefined
      : config.cases.find((c) => normalize(c.equals) === normalize(key));
  if (match) return { found: true, value: match.output };
  const fallback = config.defaultOutput?.trim();
  return fallback ? { found: true, value: fallback } : { found: false };
}
